/**
 * Prompt Variables Registry & Substitution Helper
 *
 * This module defines the allowlisted variables available for prompt templates
 * and provides a safe, pure substitution helper for replacing {{variableKey}}
 * tokens with actual values.
 *
 * Used by:
 * - Admin prompt management UI (to show available variables)
 * - Prompt template preview
 * - Threads command bar (to substitute user profile values)
 */

// ============================================================================
// Variable Definition Types
// ============================================================================

export interface PromptVariableDefinition {
  /** Machine name used in templates, e.g., 'advisorName' */
  key: string;
  /** Human-readable label for UI, e.g., 'Advisor Name' */
  label: string;
  /** Optional description explaining the variable */
  description?: string;
  /** Example value for preview/documentation */
  example: string;
  /** Category for grouping in UI */
  category: 'profile' | 'business' | 'social' | 'preferences';
}

export type PromptVariableValues = Record<string, string | null | undefined>;

// ============================================================================
// Variable Registry
// ============================================================================

/**
 * The allowlisted set of variables available for use in prompt templates.
 * These map to user profile fields and can be safely substituted at runtime.
 * 
 * To add a new variable:
 * 1. Add it here with key, label, description, example, and category
 * 2. Update extractProfileValues() in client/src/hooks/usePromptLibrary.ts to map it
 * 3. The admin UI and runtime substitution will automatically pick it up
 */
export const PROMPT_VARIABLE_REGISTRY: readonly PromptVariableDefinition[] = [
  // ============================================================================
  // Profile Variables - Basic advisor identity info
  // ============================================================================
  {
    key: 'advisorName',
    label: 'Advisor Name',
    description: 'The full display name of the travel advisor',
    example: 'Sarah Johnson',
    category: 'profile',
  },
  {
    key: 'firstName',
    label: 'First Name',
    description: "The advisor's first name",
    example: 'Sarah',
    category: 'profile',
  },
  {
    key: 'lastName',
    label: 'Last Name',
    description: "The advisor's last name",
    example: 'Johnson',
    category: 'profile',
  },
  {
    key: 'email',
    label: 'Email Address',
    description: "The advisor's email address",
    example: 'sarah@travelagency.com',
    category: 'profile',
  },
  {
    key: 'bio',
    label: 'Bio',
    description: "The advisor's professional biography",
    example: 'A passionate travel advisor with 10+ years of experience...',
    category: 'profile',
  },

  // ============================================================================
  // Business Variables - Company and professional details
  // ============================================================================
  {
    key: 'businessName',
    label: 'Business Name',
    description: 'The name of the travel business or agency',
    example: 'Wanderlust Travel Co.',
    category: 'business',
  },
  {
    key: 'agencyName',
    label: 'Agency Name',
    description: 'The parent agency or host agency name',
    example: 'WorldVia Travel Group',
    category: 'business',
  },
  {
    key: 'businessType',
    label: 'Business Type',
    description: 'The type of travel business (e.g., Travel Advisor, Tour Operator)',
    example: 'Travel Advisor',
    category: 'business',
  },
  {
    key: 'businessDescription',
    label: 'Business Description',
    description: 'A description of the travel business',
    example: 'We specialize in luxury Mediterranean experiences...',
    category: 'business',
  },
  {
    key: 'yearsInTravel',
    label: 'Years in Travel',
    description: 'Number of years of experience in the travel industry',
    example: '8',
    category: 'business',
  },
  {
    key: 'website',
    label: 'Website',
    description: "The advisor's business website URL",
    example: 'www.wanderlusttravel.com',
    category: 'business',
  },
  {
    key: 'phone',
    label: 'Phone Number',
    description: "The advisor's business phone number",
    example: '(555) 123-4567',
    category: 'business',
  },

  // ============================================================================
  // Social Variables - Social media handles
  // ============================================================================
  {
    key: 'instagramHandle',
    label: 'Instagram',
    description: 'Instagram handle (with @)',
    example: '@wanderlust_travels',
    category: 'social',
  },
  {
    key: 'facebookPage',
    label: 'Facebook',
    description: 'Facebook page URL or name',
    example: 'WanderlustTravelCo',
    category: 'social',
  },
  {
    key: 'twitterHandle',
    label: 'Twitter/X',
    description: 'Twitter/X handle (with @)',
    example: '@wanderlust_travel',
    category: 'social',
  },
  {
    key: 'linkedinProfile',
    label: 'LinkedIn',
    description: 'LinkedIn profile URL',
    example: 'linkedin.com/in/sarahjohnson',
    category: 'social',
  },
  {
    key: 'tiktokHandle',
    label: 'TikTok',
    description: 'TikTok handle (with @)',
    example: '@wanderlust_travels',
    category: 'social',
  },
  {
    key: 'youtubeChannel',
    label: 'YouTube',
    description: 'YouTube channel URL or name',
    example: 'WanderlustTravels',
    category: 'social',
  },

  // ============================================================================
  // Preferences/Specialties Variables - Professional focus and style
  // ============================================================================
  {
    key: 'destinationSpecialties',
    label: 'Destination Specialties',
    description: 'Comma-separated list of destination expertise areas',
    example: 'Italy, Greece, Mediterranean Cruises',
    category: 'preferences',
  },
  {
    key: 'primarySpecialties',
    label: 'Primary Specialties',
    description: 'Main areas of travel expertise',
    example: 'Luxury Travel, Honeymoons, River Cruises',
    category: 'preferences',
  },
  {
    key: 'clientTypes',
    label: 'Client Types',
    description: 'Types of clients the advisor typically serves',
    example: 'luxury travelers, honeymoons, family vacations',
    category: 'preferences',
  },
  {
    key: 'preferredSuppliers',
    label: 'Preferred Suppliers',
    description: 'Preferred travel suppliers and partners',
    example: 'Virtuoso, Four Seasons, Abercrombie & Kent',
    category: 'preferences',
  },
  {
    key: 'languages',
    label: 'Languages',
    description: 'Languages spoken by the advisor',
    example: 'English, Spanish, Italian',
    category: 'preferences',
  },
  {
    key: 'certifications',
    label: 'Certifications',
    description: 'Professional certifications and credentials',
    example: 'CTC, DS, Virtuoso Certified',
    category: 'preferences',
  },
  {
    key: 'writingTone',
    label: 'Writing Tone',
    description: 'Preferred writing tone',
    example: 'professional yet friendly',
    category: 'preferences',
  },
  {
    key: 'communicationStyle',
    label: 'Communication Style',
    description: 'Preferred communication approach',
    example: 'detailed and comprehensive',
    category: 'preferences',
  },
] as const;

/**
 * Map of variable keys for quick lookup
 */
export const PROMPT_VARIABLE_KEYS = PROMPT_VARIABLE_REGISTRY.map((v) => v.key);

/**
 * Get a variable definition by key
 */
export function getVariableDefinition(
  key: string,
): PromptVariableDefinition | undefined {
  return PROMPT_VARIABLE_REGISTRY.find((v) => v.key === key);
}

/**
 * All possible variable categories
 */
export const VARIABLE_CATEGORIES = ['profile', 'business', 'social', 'preferences'] as const;
export type VariableCategory = (typeof VARIABLE_CATEGORIES)[number];

/**
 * Get all variables grouped by category
 */
export function getVariablesByCategory(): Record<
  VariableCategory,
  PromptVariableDefinition[]
> {
  const result: Record<VariableCategory, PromptVariableDefinition[]> = {
    profile: [],
    business: [],
    social: [],
    preferences: [],
  };
  
  for (const variable of PROMPT_VARIABLE_REGISTRY) {
    result[variable.category].push(variable);
  }
  
  return result;
}

// ============================================================================
// Substitution Helper
// ============================================================================

/**
 * Default value used when a variable is missing from the provided values map.
 * Uses empty string for a clean, non-disruptive output.
 */
const DEFAULT_MISSING_VALUE = '';

/**
 * Regex pattern to match {{variableKey}} tokens in a template string.
 * Captures the variable key (letters, numbers, underscores) between double braces.
 */
const VARIABLE_PATTERN = /\{\{(\w+)\}\}/g;

export interface SubstitutionOptions {
  /**
   * Value to use when a variable is not found in the values map.
   * Defaults to empty string.
   */
  defaultValue?: string;

  /**
   * If true, leaves unknown variables as-is (e.g., {{unknownVar}}).
   * If false (default), replaces with defaultValue.
   */
  preserveUnknown?: boolean;
}

/**
 * Pure substitution helper that replaces {{variableKey}} tokens in a template
 * string with values from a provided map.
 *
 * This is a safe, deterministic function that:
 * - Only replaces known variable patterns
 * - Never executes code or evaluates expressions
 * - Uses safe defaults for missing values
 *
 * @param template - The template string containing {{variableKey}} placeholders
 * @param values - Map of variable keys to their values
 * @param options - Optional configuration for handling missing values
 * @returns The template with all variables substituted
 *
 * @example
 * ```ts
 * const template = "Hello {{advisorName}}, welcome to {{businessName}}!";
 * const values = { advisorName: "Sarah", businessName: "Wanderlust Travel" };
 * const result = substituteVariables(template, values);
 * // Result: "Hello Sarah, welcome to Wanderlust Travel!"
 * ```
 */
export function substituteVariables(
  template: string,
  values: PromptVariableValues,
  options: SubstitutionOptions = {},
): string {
  const { defaultValue = DEFAULT_MISSING_VALUE, preserveUnknown = false } =
    options;

  return template.replace(VARIABLE_PATTERN, (match, variableKey: string) => {
    const value = values[variableKey];

    // If value exists and is not null/undefined, use it
    if (value != null && value !== '') {
      return value;
    }

    // If preserveUnknown is true, keep the original {{variableKey}}
    if (preserveUnknown) {
      return match;
    }

    // Otherwise, use the default value
    return defaultValue;
  });
}

/**
 * Extract all variable keys used in a template string.
 *
 * @param template - The template string to analyze
 * @returns Array of unique variable keys found in the template
 *
 * @example
 * ```ts
 * const template = "Hello {{advisorName}}, your agency is {{agencyName}}.";
 * const keys = extractVariableKeys(template);
 * // Result: ['advisorName', 'agencyName']
 * ```
 */
export function extractVariableKeys(template: string): string[] {
  const matches = template.matchAll(VARIABLE_PATTERN);
  const keys = new Set<string>();

  for (const match of matches) {
    keys.add(match[1]);
  }

  return Array.from(keys);
}

/**
 * Validate that all variables in a template are from the allowed registry.
 *
 * @param template - The template string to validate
 * @returns Object with isValid flag and any unknown variable keys
 */
export function validateTemplateVariables(template: string): {
  isValid: boolean;
  unknownVariables: string[];
  usedVariables: string[];
} {
  const usedVariables = extractVariableKeys(template);
  const unknownVariables = usedVariables.filter(
    (key) => !PROMPT_VARIABLE_KEYS.includes(key),
  );

  return {
    isValid: unknownVariables.length === 0,
    unknownVariables,
    usedVariables,
  };
}

/**
 * Generate example values for all registered variables.
 * Useful for preview functionality.
 */
export function getExampleValues(): PromptVariableValues {
  return PROMPT_VARIABLE_REGISTRY.reduce(
    (acc, variable) => {
      acc[variable.key] = variable.example;
      return acc;
    },
    {} as PromptVariableValues,
  );
}

